// lib/grid-generation-script.ts
import type { Layer, Rect, ScriptInstance } from '#types/types';
import { normalizeSeedVR2OutputMegaPixels, SEEDVR2_OUTPUT_PIXELS, type SeedVR2OutputMegaPixels } from '#lib/seedvr2';
import { STUDIO_LAYER_MAX_SIDE_PIXELS } from '#lib/studio-layer-transform';

export type GridOrder = 'centerOut' | 'rowMajor';
export type GridModelPreset = 'current' | 'enhance' | 'upscale';
export type GridSweepMode = 'classicGrid' | 'overlapFromCenter';
export type GridHarmonizationMode = 'off' | 'soft' | 'strict';
export type GridEdgeFadeRadius = 'auto' | 0 | 32 | 64 | 96;
export type GridTileShape = 'matchFrame' | 'square';

export interface GridGenerationRuntime {
  gridSize: number;
  includeCenterProbeForEven: boolean;
  clampToFrame: boolean;
  order: GridOrder;
  sweepMode: GridSweepMode;
  overlapScale: number;
  tileHarmonization: GridHarmonizationMode;
  preserveHighRes: boolean;
  tileEdgeFadeRadius: GridEdgeFadeRadius;
  tileShape: GridTileShape;
}

export interface GridGenerationPayload {
  runtime?: Partial<GridGenerationRuntime>;
  modelPreset?: GridModelPreset;
  modelId?: string;
  source?: string;
}

const GRID_MAX_SECTIONS_PER_AXIS = 2;

export interface GridSection {
  index: number;
  rect: Rect;
  pixelWidth: number;
  pixelHeight: number;
}

export interface GridSectionPlan {
  pixelWidth: number;
  pixelHeight: number;
  sections: GridSection[];
}

/** What every tile command carries so whichever tile lands first can create the anchors, and a resume can rebuild the grid. */
export interface GridBatchLaunch {
  baseRect: Rect;
  runtime: GridGenerationRuntime;
  modelId: string;
  modelPreset?: GridModelPreset;
  modelParams?: Record<string, unknown> | null;
  /** Fixed-size anchors decided before the first tile; absent = one anchor growing by merge. */
  sections?: GridSectionPlan;
}

/** Persisted on each anchor layer as params.gridBatch: the batch outlives the tab that ran it. */
export interface GridBatch {
  batchId: string;
  tileCount: number;
  /** Tiles painted into this anchor; a tile counts as done once recorded on any anchor of the batch. */
  completedTileIndexes: number[];
  launch?: GridBatchLaunch;
  sectionIndex?: number;
}

export const readGridBatch = (params: unknown): GridBatch | null => {
  const batch = (params as { gridBatch?: Partial<GridBatch> } | null | undefined)?.gridBatch;
  if (!batch || typeof batch.batchId !== 'string' || !batch.batchId) return null;
  if (typeof batch.tileCount !== 'number' || !(batch.tileCount > 0)) return null;
  const completedTileIndexes = Array.isArray(batch.completedTileIndexes)
    ? batch.completedTileIndexes.filter((index): index is number => Number.isInteger(index))
    : [];
  return {
    batchId: batch.batchId,
    tileCount: batch.tileCount,
    completedTileIndexes,
    launch: batch.launch,
    ...(Number.isInteger(batch.sectionIndex) ? { sectionIndex: batch.sectionIndex } : {}),
  };
};

export const findGridBatchLayers = (layers: Layer[], batchId: string): Layer[] => (
  layers.filter(layer => readGridBatch(layer.params)?.batchId === batchId)
);

export const findGridBatchAnchor = (layers: Layer[], batchId: string): Layer | null => (
  findGridBatchLayers(layers, batchId)[0] ?? null
);

/** skipRender is runtime-only and set while this tab runs the batch, so a reload makes it resumable. */
export const readResumableGridBatch = (
  layer: Layer,
  workspaceLayers: Layer[],
): (GridBatch & { launch: GridBatchLaunch }) | null => {
  const batch = readGridBatch(layer.params);
  if (!batch?.launch) return null;
  const batchLayers = findGridBatchLayers(workspaceLayers, batch.batchId);
  if (batchLayers.some(candidate => candidate.skipRender === true)) return null;
  const completedTileIndexes = [...new Set(batchLayers.flatMap(candidate => readGridBatch(candidate.params)?.completedTileIndexes ?? []))]
    .sort((a, b) => a - b);
  return completedTileIndexes.length < batch.tileCount
    ? { ...batch, completedTileIndexes, launch: batch.launch }
    : null;
};

const splitPixels = (total: number): Array<{ start: number; size: number }> => {
  const count = Math.ceil(total / STUDIO_LAYER_MAX_SIDE_PIXELS);
  const boundary = (index: number) => Math.round((total * index) / count);
  return Array.from({ length: count }, (_, index) => ({ start: boundary(index), size: boundary(index + 1) - boundary(index) }));
};

/**
 * Sized before the first tile from the densest tile, so no anchor ever grows or is resampled.
 * Each tile is expected to render tileOutputPixels whatever its area (SeedVR2's contract).
 */
export const planGridSections = (
  baseRect: Rect,
  runtime: GridGenerationRuntime,
  tileOutputPixels: number,
): GridSectionPlan | null => {
  if (!(baseRect.w > 0 && baseRect.h > 0 && tileOutputPixels > 0)) return null;
  const zones = buildGridScriptTargetZones(baseRect, runtime);
  const densestTile = Math.max(0, ...zones.map(zone => Math.sqrt(tileOutputPixels / (zone.rect.w * zone.rect.h))));
  if (!(densestTile > 0)) return null;
  const maxSide = STUDIO_LAYER_MAX_SIDE_PIXELS * GRID_MAX_SECTIONS_PER_AXIS;
  const density = Math.min(densestTile, maxSide / baseRect.w, maxSide / baseRect.h);
  const pixelWidth = Math.min(maxSide, Math.round(baseRect.w * density));
  const pixelHeight = Math.min(maxSide, Math.round(baseRect.h * density));
  const columns = splitPixels(pixelWidth);
  const rows = splitPixels(pixelHeight);
  const sections = rows.flatMap(row => columns.map(column => ({ row, column })))
    .map(({ row, column }, index): GridSection => ({
      index,
      rect: {
        x: baseRect.x + column.start / density,
        y: baseRect.y + row.start / density,
        w: column.size / density,
        h: row.size / density,
      },
      pixelWidth: column.size,
      pixelHeight: row.size,
    }));
  return { pixelWidth, pixelHeight, sections };
};

const resolveGridTileOutputPixels = (modelId: string, modelParams: Record<string, unknown> | null | undefined): number | null => (
  modelId === 'seedvr2' ? SEEDVR2_OUTPUT_PIXELS[normalizeSeedVR2OutputMegaPixels(modelParams?.outputMegaPixels)] : null
);

export const planGridSectionsForModel = (
  baseRect: Rect,
  runtime: GridGenerationRuntime,
  modelId: string,
  modelParams: Record<string, unknown> | null | undefined,
): GridSectionPlan | null => {
  const tileOutputPixels = resolveGridTileOutputPixels(modelId, modelParams);
  return tileOutputPixels ? planGridSections(baseRect, runtime, tileOutputPixels) : null;
};

export const GRID_SIZE_MIN = 2;
export const GRID_SIZE_MAX = 12;
const GRID_SIZE_DEFAULT_FULL_FRAME = 4;
const GRID_SIZE_DEFAULT_MEDIUM = 3;
const GRID_SIZE_DEFAULT_SMALL = 2;
const GRID_SIZE_MEDIUM_THRESHOLD = (3 * 3) / (4 * 4); // 9 / 16
const GRID_SIZE_SMALL_THRESHOLD = (2 * 2) / (4 * 4); // 4 / 16
export const TILE_OVERLAP_SCALE_MIN = 1;
export const TILE_OVERLAP_SCALE_MAX = 1.3;
export const TILE_OVERLAP_SCALE_STEP = 0.01;
const TILE_OVERLAP_SCALE_DEFAULT = 1.10;

export const DEFAULT_GRID_GENERATION_RUNTIME: GridGenerationRuntime = {
  gridSize: 3,
  includeCenterProbeForEven: false,
  clampToFrame: true,
  order: 'rowMajor',
  sweepMode: 'classicGrid',
  overlapScale: TILE_OVERLAP_SCALE_DEFAULT,
  tileHarmonization: 'strict',
  preserveHighRes: false,
  tileEdgeFadeRadius: 'auto',
  tileShape: 'matchFrame',
};

// The 8K and 16K Upscale powers are fixed launches, declared beside the defaults they derive from
// so the kernel that runs them, the menu that prices them and the test that guards them read one source.
export const SWEEP_UPSCALE_MODEL_ID = 'seedvr2';
export type SweepUpscaleGridSize = 2 | 3;
export const SWEEP_UPSCALE_OUTPUT_MEGAPIXELS: Record<SweepUpscaleGridSize, SeedVR2OutputMegaPixels> = { 2: 28, 3: 32 };
export const buildSweepUpscaleRuntime = (gridSize: SweepUpscaleGridSize): GridGenerationRuntime => (
  { ...DEFAULT_GRID_GENERATION_RUNTIME, gridSize }
);

export const normalizeGridEdgeFadeRadius = (
  value: unknown,
  fallback: GridEdgeFadeRadius = DEFAULT_GRID_GENERATION_RUNTIME.tileEdgeFadeRadius
): GridEdgeFadeRadius => (
  value === 'auto' || value === 0 || value === 32 || value === 64 || value === 96 ? value : fallback
);

export const resolveDefaultGridEdgeFadeRadius = (
  modelPreset: GridModelPreset | undefined,
  value: unknown
): GridEdgeFadeRadius => {
  const isQuickPowerPreset = modelPreset === 'enhance' || modelPreset === 'upscale';
  return normalizeGridEdgeFadeRadius(
    value,
    isQuickPowerPreset ? 'auto' : DEFAULT_GRID_GENERATION_RUNTIME.tileEdgeFadeRadius,
  );
};

export const resolveDefaultGridPreserveHighRes = (
  modelPreset: GridModelPreset | undefined,
  value: unknown
): boolean => (
  typeof value === 'boolean'
    ? value
    : modelPreset === 'enhance' || modelPreset === 'upscale'
);

export const normalizeGridTileShape = (
  value: unknown,
  fallback: GridTileShape = DEFAULT_GRID_GENERATION_RUNTIME.tileShape
): GridTileShape => value === 'square' || value === 'matchFrame' ? value : fallback;

export const normalizeGridHarmonizationMode = (
  value: unknown,
  fallback: GridHarmonizationMode = DEFAULT_GRID_GENERATION_RUNTIME.tileHarmonization
): GridHarmonizationMode => (
  value === 'off' || value === 'soft' || value === 'strict' ? value : fallback
);

export const resolveDefaultGridHarmonizationMode = (
  modelPreset: GridModelPreset | undefined,
  value: unknown
): GridHarmonizationMode => normalizeGridHarmonizationMode(
  value,
  modelPreset === 'upscale' ? 'strict' : DEFAULT_GRID_GENERATION_RUNTIME.tileHarmonization
);

const nextCommandId = () => `ga-grid-cmd-${crypto.randomUUID()}`;

const makeRectCommand = (description: string, rect: Rect, clampToFrame: boolean) => ({
  id: nextCommandId(),
  action_type: 'SET_GENERATION_AREA_RECT',
  parameters: {
    x: rect.x,
    y: rect.y,
    width: rect.w,
    height: rect.h,
    clampToFrame,
  },
  description,
  status: 'pending' as const,
});

const makeGenerateCommand = (
  description: string,
  parameters: Record<string, unknown> = {}
) => ({
  id: nextCommandId(),
  action_type: 'GENERATE_MEDIA',
  parameters,
  description,
  status: 'pending' as const,
});

const makeSetModelParameterCommand = (
  modelId: string,
  name: string,
  value: unknown
) => ({
  id: nextCommandId(),
  action_type: 'SET_MODEL_PARAMETER',
  parameters: {
    modelId,
    name,
    value,
  },
  description: `Set ${modelId}.${name}`,
  status: 'pending' as const,
});

const makeClearReferenceImagesCommand = () => ({
  id: nextCommandId(),
  action_type: 'SET_MODEL_REFERENCE_IMAGES',
  parameters: {
    urls: [],
    mode: 'replace',
  },
  description: 'Clear reference images for quick power parity',
  status: 'pending' as const,
});

const QUICK_POWER_PRESET_PARAMS: Array<{ name: string; value: unknown }> = [
  { name: 'prompt', value: '' },
  { name: 'seed', value: -1 },
  { name: 'resolution', value: '1 MP' },
  { name: 'aspect_ratio', value: 'match_input_image' },
  { name: 'harmonize', value: 'On' },
  { name: 'preserve_high_res', value: true },
  { name: 'alpha_radius', value: 64 },
];

export interface GridSweepZone {
  description: string;
  rect: Rect;
}

export const normalizeGridOverlapScale = (value: number): number => {
  if (!Number.isFinite(value)) return TILE_OVERLAP_SCALE_DEFAULT;
  if (value < TILE_OVERLAP_SCALE_MIN) return TILE_OVERLAP_SCALE_MIN;
  if (value > TILE_OVERLAP_SCALE_MAX) return TILE_OVERLAP_SCALE_MAX;
  return value;
};

export const resolveDefaultGridSizeFromCoverage = (
  generationRect?: Rect | null,
  frameRect?: Rect | null
): number => {
  if (!generationRect || !frameRect) {
    return DEFAULT_GRID_GENERATION_RUNTIME.gridSize;
  }
  if (!(generationRect.w > 0 && generationRect.h > 0 && frameRect.w > 0 && frameRect.h > 0)) {
    return DEFAULT_GRID_GENERATION_RUNTIME.gridSize;
  }

  const generationArea = generationRect.w * generationRect.h;
  const frameArea = frameRect.w * frameRect.h;
  if (!(generationArea > 0 && frameArea > 0)) {
    return DEFAULT_GRID_GENERATION_RUNTIME.gridSize;
  }

  const coverage = Math.max(0, Math.min(1, generationArea / frameArea));
  if (coverage < GRID_SIZE_SMALL_THRESHOLD) return GRID_SIZE_DEFAULT_SMALL;
  if (coverage < GRID_SIZE_MEDIUM_THRESHOLD) return GRID_SIZE_DEFAULT_MEDIUM;
  return GRID_SIZE_DEFAULT_FULL_FRAME;
};

const scaleRectFromCenter = (rect: Rect, scale: number): Rect => {
  if (!Number.isFinite(scale) || scale <= 1) return rect;
  const nextW = rect.w * scale;
  const nextH = rect.h * scale;
  return {
    x: rect.x - (nextW - rect.w) / 2,
    y: rect.y - (nextH - rect.h) / 2,
    w: nextW,
    h: nextH,
  };
};

const buildSquareGridZones = (baseRect: Rect, runtime: GridGenerationRuntime): GridSweepZone[] => {
  const { gridSize, includeCenterProbeForEven, order } = runtime;
  const shortSide = Math.min(baseRect.w, baseRect.h);
  const tileSide = shortSide / gridSize;
  const isPortrait = baseRect.h > baseRect.w;
  const shortCount = gridSize;
  const longCount = Math.max(shortCount, Math.ceil(Math.max(baseRect.w, baseRect.h) / tileSide));
  const columns = isPortrait ? shortCount : longCount;
  const rows = isPortrait ? longCount : shortCount;
  const xStep = columns > 1 ? (baseRect.w - tileSide) / (columns - 1) : 0;
  const yStep = rows > 1 ? (baseRect.h - tileSide) / (rows - 1) : 0;
  const cells: Array<{ row: number; col: number; rect: Rect; dist2: number }> = [];
  const centerX = (columns - 1) / 2;
  const centerY = (rows - 1) / 2;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const rect: Rect = {
        x: baseRect.x + col * xStep,
        y: baseRect.y + row * yStep,
        w: tileSide,
        h: tileSide,
      };
      cells.push({
        row,
        col,
        rect,
        dist2: Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2),
      });
    }
  }

  if (order === 'centerOut') {
    cells.sort((a, b) => {
      if (a.dist2 !== b.dist2) return a.dist2 - b.dist2;
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });
  }

  const zones = cells.map((cell) => ({
    description: `GA square ${columns}x${rows} r${cell.row + 1} c${cell.col + 1}`,
    rect: cell.rect,
  }));

  if (gridSize % 2 === 0 && includeCenterProbeForEven) {
    zones.unshift({
      description: 'GA square center probe',
      rect: {
        x: baseRect.x + (baseRect.w - tileSide) / 2,
        y: baseRect.y + (baseRect.h - tileSide) / 2,
        w: tileSide,
        h: tileSide,
      },
    });
  }

  return zones;
};

const buildClassicGridZones = (baseRect: Rect, runtime: GridGenerationRuntime): GridSweepZone[] => {
  const { gridSize, includeCenterProbeForEven, order } = runtime;
  const cellW = baseRect.w / gridSize;
  const cellH = baseRect.h / gridSize;
  const centerX = (gridSize - 1) / 2;
  const centerY = (gridSize - 1) / 2;

  const cells: Array<{ row: number; col: number; rect: Rect; dist2: number }> = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const rect: Rect = {
        x: baseRect.x + col * cellW,
        y: baseRect.y + row * cellH,
        w: cellW,
        h: cellH,
      };
      const dist2 = Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2);
      cells.push({ row, col, rect, dist2 });
    }
  }

  if (order === 'centerOut') {
    cells.sort((a, b) => {
      if (a.dist2 !== b.dist2) return a.dist2 - b.dist2;
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });
  } else {
    cells.sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });
  }

  const zones: GridSweepZone[] = [];

  if (gridSize % 2 === 0 && includeCenterProbeForEven) {
    zones.push({
      description: 'GA center probe',
      rect: {
        x: baseRect.x + (baseRect.w - cellW) / 2,
        y: baseRect.y + (baseRect.h - cellH) / 2,
        w: cellW,
        h: cellH,
      },
    });
  }

  for (const cell of cells) {
    zones.push({
      description: `GA grid ${gridSize}x${gridSize} r${cell.row + 1} c${cell.col + 1}`,
      rect: cell.rect,
    });
  }

  return zones;
};

const buildOverlapZones = (baseRect: Rect, runtime: GridGenerationRuntime): GridSweepZone[] => {
  const { gridSize } = runtime;
  const maxRing = Math.max(1, gridSize - 1);
  const tileW = baseRect.w / gridSize;
  const tileH = baseRect.h / gridSize;

  const centerCx = baseRect.x + baseRect.w / 2;
  const centerCy = baseRect.y + baseRect.h / 2;
  const stepX = (baseRect.w - tileW) / (2 * maxRing);
  const stepY = (baseRect.h - tileH) / (2 * maxRing);

  const zones: GridSweepZone[] = [
    {
      description: 'GA overlap center',
      rect: {
        x: centerCx - tileW / 2,
        y: centerCy - tileH / 2,
        w: tileW,
        h: tileH,
      },
    },
  ];

  for (let ring = 1; ring <= maxRing; ring++) {
    const ringPoints: Array<{ dx: number; dy: number; angle: number }> = [];

    for (let dx = -ring; dx <= ring; dx++) {
      for (let dy = -ring; dy <= ring; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
        if ((Math.abs(dx + dy) % 2) !== 0) continue;
        ringPoints.push({
          dx,
          dy,
          angle: Math.atan2(dy, dx),
        });
      }
    }

    ringPoints.sort((a, b) => a.angle - b.angle);

    ringPoints.forEach((point, index) => {
      const cx = centerCx + point.dx * stepX;
      const cy = centerCy + point.dy * stepY;
      zones.push({
        description: `GA overlap r${ring} n${index + 1}`,
        rect: {
          x: cx - tileW / 2,
          y: cy - tileH / 2,
          w: tileW,
          h: tileH,
        },
      });
    });
  }

  return zones;
};

const buildGridSweepBaseZones = (
  baseRect: Rect,
  runtime: GridGenerationRuntime
): GridSweepZone[] => {
  if (runtime.tileShape === 'square') return buildSquareGridZones(baseRect, runtime);
  return runtime.sweepMode === 'overlapFromCenter'
    ? buildOverlapZones(baseRect, runtime)
    : buildClassicGridZones(baseRect, runtime);
};

const clampRectToFrame = (rect: Rect, frameRect: Rect): Rect | null => {
  const left = Math.max(rect.x, frameRect.x);
  const top = Math.max(rect.y, frameRect.y);
  const right = Math.min(rect.x + rect.w, frameRect.x + frameRect.w);
  const bottom = Math.min(rect.y + rect.h, frameRect.y + frameRect.h);
  const width = right - left;
  const height = bottom - top;
  if (!(width > 0 && height > 0)) return null;
  return {
    x: left,
    y: top,
    w: width,
    h: height,
  };
};

const buildGridScriptTargetZones = (
  baseRect: Rect,
  runtime: GridGenerationRuntime
): GridSweepZone[] => {
  const baseZones = buildGridSweepBaseZones(baseRect, runtime);
  const overlapScale = normalizeGridOverlapScale(runtime.overlapScale);
  const clampedZones: GridSweepZone[] = [];
  for (const zone of baseZones) {
    const scaledRect = scaleRectFromCenter(zone.rect, overlapScale);
    const clampedToGenerationRect = clampRectToFrame(scaledRect, baseRect);
    if (!clampedToGenerationRect) continue;
    clampedZones.push({
      description: zone.description,
      rect: clampedToGenerationRect,
    });
  }
  return clampedZones;
};

export const buildGridPreviewZones = (
  baseRect: Rect,
  runtime: GridGenerationRuntime,
  frameRect?: Rect | null
): GridSweepZone[] => {
  const scriptTargetZones = buildGridScriptTargetZones(baseRect, runtime);
  if (!runtime.clampToFrame || !frameRect) {
    return scriptTargetZones;
  }

  const clampedZones: GridSweepZone[] = [];
  for (const zone of scriptTargetZones) {
    const clampedRect = clampRectToFrame(zone.rect, frameRect);
    if (!clampedRect) continue;
    clampedZones.push({
      description: zone.description,
      rect: clampedRect,
    });
  }
  return clampedZones;
};

export const parseAndValidateGridSize = (value: string): number | null => {
  const parsedGridSize = Number.parseInt(value, 10);
  if (!Number.isFinite(parsedGridSize)) return null;
  if (parsedGridSize < GRID_SIZE_MIN || parsedGridSize > GRID_SIZE_MAX) return null;
  return parsedGridSize;
};

export const computeGridFrameCount = (
  gridSize: number,
  includeCenterProbeForEven: boolean,
  sweepMode: GridSweepMode
): number => {
  if (sweepMode === 'overlapFromCenter') {
    return 1 + (2 * gridSize * (gridSize - 1));
  }
  return gridSize * gridSize + (gridSize % 2 === 0 && includeCenterProbeForEven ? 1 : 0);
};

export const resolveGridGenerationModelId = (
  payload: GridGenerationPayload | undefined,
  currentModelId: string
): string => {
  if (payload?.modelPreset === 'enhance') {
    return payload.modelId?.trim() || 'fater-enhance';
  }
  if (payload?.modelPreset === 'upscale') {
    return payload.modelId?.trim() || 'fater-upscale';
  }
  return payload?.modelId?.trim() || currentModelId;
};

export const buildGridScript = (
  baseRect: Rect,
  runtime: GridGenerationRuntime,
  generationModelId: string,
  modelPreset?: GridModelPreset,
  options: {
    batchId?: string;
    modelParams?: Record<string, unknown> | null;
    skipTileIndexes?: number[];
    /** A resume replays the stored plan (null = merge mode) instead of planning again. */
    sections?: GridSectionPlan | null;
  } = {}
): ScriptInstance => {
  const { gridSize, clampToFrame, sweepMode } = runtime;
  const zones = buildGridScriptTargetZones(baseRect, runtime);

  const commands: ScriptInstance['commands'] = [
    {
      id: nextCommandId(),
      action_type: 'SET_ASPECT_RATIO',
      parameters: {
        ratio: 'free',
      },
      description: 'Remove GA aspect ratio constraint',
      status: 'pending',
    },
  ];

  if (generationModelId) {
    commands.push({
      id: nextCommandId(),
      action_type: 'SELECT_MODEL',
      parameters: {
        modelId: generationModelId,
      },
      description: `Select model ${generationModelId}`,
      status: 'pending',
    });

    if (modelPreset === 'enhance' || modelPreset === 'upscale') {
      commands.push(makeClearReferenceImagesCommand());
      for (const { name, value } of QUICK_POWER_PRESET_PARAMS) {
        commands.push(makeSetModelParameterCommand(generationModelId, name, value));
      }
    }
  }

  const batchId = options.batchId ?? crypto.randomUUID();
  const skipTileIndexes = new Set(options.skipTileIndexes ?? []);
  const sections = (options.sections !== undefined
    ? options.sections
    : planGridSectionsForModel(baseRect, runtime, generationModelId, options.modelParams)) ?? undefined;
  const launch: GridBatchLaunch = {
    baseRect,
    runtime,
    modelId: generationModelId,
    modelPreset,
    modelParams: options.modelParams ?? null,
    ...(sections ? { sections } : {}),
  };
  const harmonizeMode = normalizeGridHarmonizationMode(runtime.tileHarmonization);
  const pushZoneCommands = (rect: Rect, index: number) => {
    const tileLabel = `tile ${index + 1} of ${zones.length}`;
    commands.push(makeRectCommand(`Framing ${tileLabel}`, rect, clampToFrame));
    commands.push(
      makeGenerateCommand(
        `Generating ${tileLabel}`,
        {
          parameterOverrides: {
            harmonize: harmonizeMode === 'off' ? 'Off' : 'On',
            harmonize_profile: harmonizeMode === 'strict' ? 'strict' : 'soft',
            preserve_high_res: runtime.preserveHighRes,
            alpha_radius: runtime.tileEdgeFadeRadius === 'auto' ? 0 : runtime.tileEdgeFadeRadius,
          },
          generationOptions: {
            layerConsolidation: {
              mode: 'mergeIntoBatchAnchor',
              batchId,
              tileIndex: index,
              tileCount: zones.length,
              tileEdgeFadeRadiusMode: runtime.tileEdgeFadeRadius === 'auto'
                ? 'tileOverlapMargin'
                : 'fixed',
              tileOverlapScale: runtime.overlapScale,
              grid: launch,
            },
          },
        }
      )
    );
  };

  for (const [index, zone] of zones.entries()) {
    if (!skipTileIndexes.has(index)) pushZoneCommands(zone.rect, index);
  }

  commands.push(
    makeRectCommand(
      'Restore initial generation area',
      baseRect,
      true
    )
  );

  const modeLabel = sweepMode === 'overlapFromCenter' ? 'overlap sweep' : 'classic grid sweep';
  const sweepLabel = `GA ${gridSize}x${gridSize} ${modeLabel}: free ratio + GENERATE_MEDIA per zone`;

  return {
    id: `ga-tiles-script-${crypto.randomUUID()}`,
    label: sweepLabel,
    autorun: true,
    isAgentGenerated: false,
    isManuallyInvoked: true,
    commands,
  };
};
